import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
  CTA,
  CustomInput,
  CustomSelect,
  NoResult,
  Text,
} from '../../../ui/atoms';
import {
  FiltersContainer,
  Filter,
  SelectsContainer,
  CourseItem,
  ResultsSorting,
  Legend,
} from './style';
import { flattenCategories } from '../../../utils/categories';
import {
  COURSE_LIST_RESET,
  GLOBAL_COURSE_LIST_GET,
} from '../../redux/actions/courselist';
import Pagination from '../../../ui/atoms/Pagination';
import SortBy from '../../../ui/atoms/SortBy';
import { withMediaQueries } from '../../hoc/withMediaQueries';
import { StickyFiltersWrapper } from '../../../ui/atoms/CTA/style';
import { MODAL_OPEN } from '../../redux/actions';
import ModalFilters, {
  ModalFiltersId,
} from '../../../ui/atoms/Modals/ModalFilters';
import storage from '../../api/storage';
import CustomTooltip from '../../../ui/atoms/CustomTooltip';

const ModuleResults = ({
  mediaIsPhone,
  currentCatalog,
  currentArea,
  active = true,
  list,
  pager,
  getCourseList,
  courseListReset,
  modalOpen,
}) => {
  const [subjects, setSubjects] = useState([]);
  // filters
  const [selectedFilters, setSelectedFilters] = useState({});
  const [searchValue, setSearchValue] = useState();

  useEffect(() => {
    return () => {
      courseListReset();
      setSelectedFilters({});
      setSubjects([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetCourses = (page) => {
    if (currentCatalog?.id) {
      const parsedFilters = {
        ...selectedFilters,
        catalog_id: currentCatalog?.id,
        categories: selectedFilters?.subject || currentArea?.id,
        categoryType: selectedFilters?.subject
          ? 'single_categories'
          : 'parent_categories',
        is_module: true,
        page,
        typology: 'course',
      };
      storage.write('moduleFilters', parsedFilters);
      delete parsedFilters?.subject;
      getCourseList(parsedFilters);
    }
  };

  useEffect(() => {
    if (currentArea?.id) {
      const newSubjects = flattenCategories(currentArea?.children, 0, true);
      setSubjects(newSubjects);
    } else setSubjects([]);
    setSelectedFilters({ ...selectedFilters, subject: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArea]);

  useEffect(() => {
    if (Object.keys(selectedFilters)?.length > 0) {
      handleGetCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, currentCatalog]);

  const onClickSearch = () => {
    setSelectedFilters({ ...selectedFilters, search_text: searchValue });
  };

  const handleOnChange = ({ key, value }) => {
    const newFilters = { ...selectedFilters };
    newFilters[key] = value?.value;
    setSelectedFilters(newFilters);
  };

  const loadMore = (page) => handleGetCourses(page);
  const setItemsPerPage = (v) =>
    handleOnChange({ key: 'items_per_page', value: v });

  return (
    <>
      {mediaIsPhone ? (
        <StickyFiltersWrapper mb={16} stickyPosition={1}>
          <CustomInput
            placeholder="Cosa vuoi cercare?..."
            isSearch
            value=""
            icon="search-sm"
            isClearable
            onChange={(v) => setSearchValue(v)}
            onClickIcon={onClickSearch}
            onKeyPress={(event) => {
              if (event.key === 'Enter') onClickSearch();
            }}
          />
          <CTA
            classes="small-btn"
            btnText="Filtri"
            type="secondary"
            fullsize
            onClick={() =>
              modalOpen(ModalFiltersId, {
                filters: {
                  subject: subjects,
                },
                activeFilters: selectedFilters,
              })
            }
          />
        </StickyFiltersWrapper>
      ) : (
        <>
          <FiltersContainer>
            <SelectsContainer>
              <Filter>
                <CustomInput
                  placeholder="Cosa vuoi cercare?..."
                  isSearch
                  value=""
                  icon="search-sm"
                  isClearable
                  onChange={(v) => setSearchValue(v)}
                  onClickIcon={onClickSearch}
                  onKeyPress={(event) => {
                    if (event.key === 'Enter') onClickSearch();
                  }}
                />
              </Filter>
              {subjects?.length > 0 && (
                <Filter>
                  <CustomSelect
                    placeholder="Materia"
                    options={subjects}
                    isClearable
                    onChange={(v) =>
                      setSelectedFilters({
                        ...selectedFilters,
                        subject: v?.value,
                      })
                    }
                    selected={
                      subjects?.find(
                        (s) => s.value === selectedFilters?.subject
                      ) ?? null
                    }
                  />
                </Filter>
              )}
            </SelectsContainer>
          </FiltersContainer>
          <ResultsSorting>
            <Text type="b3">
              {pager?.total_items || 0} attività disponibili
            </Text>
            <SortBy
              onChange={(v) => {
                setSelectedFilters({
                  ...selectedFilters,
                  sort_by: v?.value,
                });
              }}
            />
          </ResultsSorting>
        </>
      )}
      {list.length <= 0 ? (
        <NoResult />
      ) : mediaIsPhone ? (
        <>
          {(list || []).map((item, i) => (
            <CourseItem key={`course-${i}`}>
              <Text type="h3.5">Codice</Text>
              <Text type="b3" className="code">
                {item?.course_code}
              </Text>
              <Text type="h3.5">Titolo</Text>
              <Text type="h3" className="line-clamp">
                {item?.course_title}
              </Text>
              <Text type="h3.5">Materia</Text>
              <Text type="b3">{item?.course_category}</Text>
              {item?.course_tooltip && (
                <>
                  <div />
                  <div className="tooltip-wrapper">
                    <CustomTooltip tooltipKey={item.course_tooltip} />
                  </div>
                </>
              )}
            </CourseItem>
          ))}
        </>
      ) : (
        <>
          <Legend>
            <Text type="h3.5">Codice</Text>
            <Text type="h3.5">Titolo</Text>
            <Text type="h3.5">Materia</Text>
          </Legend>
          {(list || []).map((item, i) => (
            <CourseItem key={`course-${i}`}>
              <Text type="b3" className="code">
                {item?.course_code}
              </Text>
              <Text type="h3" className="line-clamp">
                {item?.course_title}
              </Text>
              <Text type="b3">{item?.course_category}</Text>
              {item?.course_tooltip && (
                <div className="tooltip-wrapper">
                  <CustomTooltip tooltipKey={item.course_tooltip} />
                </div>
              )}
            </CourseItem>
          ))}
        </>
      )}
      <Pagination
        pt={32}
        pager={pager}
        callBack={loadMore}
        setItemsPerPage={setItemsPerPage}
      />
      {mediaIsPhone && active && (
        <ModalFilters
          onClick={(newFilters) =>
            setSelectedFilters({
              ...selectedFilters,
              ...newFilters,
              title: selectedFilters.title,
            })
          }
        />
      )}
    </>
  );
};

ModuleResults.propTypes = {
  mediaIsPhone: PropTypes.bool,
  currentCatalog: PropTypes.object,
  currentArea: PropTypes.object,
  active: PropTypes.bool,
  // HOC (connect, state)
  list: PropTypes.array.isRequired,
  pager: PropTypes.object.isRequired,
  // HOC (connect, dispatch)
  getCourseList: PropTypes.func.isRequired,
  courseListReset: PropTypes.func.isRequired,
  modalOpen: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => {
      const { list = [], pager = {} } = state.courselist.modules;
      return {
        list,
        pager,
      };
    },
    (dispatch) => ({
      getCourseList: (filters) =>
        dispatch({
          type: GLOBAL_COURSE_LIST_GET._REQUEST,
          filters,
        }),
      courseListReset: () => dispatch({ type: COURSE_LIST_RESET }),
      modalOpen: (id, payload) => dispatch({ type: MODAL_OPEN, id, payload }),
    })
  ),
  withMediaQueries
)(ModuleResults);
